import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, from, forkJoin, of, throwError } from 'rxjs';
import { switchMap, map, tap, catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ApiResponse, ApiPaginatedResponse, ApiErrorCode } from './api.interface';
import { Project, ProjectStatusModel, CompanyProjectAssignment, Company, UserProjectAssignment, ProjectCompany, ProjectRequest, ProjectRequestResponse, ProjectRequestListResponse, ProjectRequestDetail, CreatePiledModuleRequest, PiledModule, CreatePullTestingModuleRequest, PullTestingModule, UpdatePullTestingModuleRequest, UpdatePiledModuleRequest, ConfiguredModuleRow, ModuleInvoicedStatus, ModuleType, stripProjectModuleEmbeds, ProjectDirectory } from 'shared_models';
import { LoggerService } from './logger.service';

/**
 * Map state interface for backend rendering
 * Captures the current map view so backend can render the same geographic view at a standard size
 */
export interface MapState {
  center: [number, number]; // [longitude, latitude]
  zoom: number;
  pitch: number;
  bearing: number;
  bounds?: {
    sw: [number, number]; // [longitude, latitude] - southwest corner
    ne: [number, number]; // [longitude, latitude] - northeast corner
  };
}

// Interface for daily progress response
export interface DailyProgressData {
  installedPilingsCount: number;
  refusedPilingsCount: number;
  averageTimeBetweenActions: number;
  periodStart: string;
  periodEnd: string;
}

export interface ListConfiguredModulesFilters {
  invoicedStatus?: ModuleInvoicedStatus[];
  companyName?: string;
  status?: string;
  moduleType?: ModuleType;
  limit?: number;
  startKey?: string;
}
import { BaseApiService } from './base-api.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectService extends BaseApiService {
  private dailyProgressCache = new Map<string, ApiResponse<DailyProgressData>>();
  private projectCache = new Map<string, Project>();
  private userProjectAssignmentCache = new Map<string, UserProjectAssignment>();

  constructor(
    http: HttpClient,
    private authService: AuthService,
    protected override logger: LoggerService
  ) {
    super(http, logger);
  }

  private getPaginationParams(limit?: number, startKey?: string): HttpParams {
    let params = new HttpParams();
    if (limit) params = params.set('limit', limit.toString());
    if (startKey) params = params.set('startKey', startKey);
    return params;
  }

  // Get all projects (GlobalAdmin only)
  getAllProjects(limit?: number, lastEvaluatedKey?: string): Observable<ApiPaginatedResponse<Project[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = this.getPaginationParams(limit, lastEvaluatedKey);
        const url = `${this.apiUrl}/projects`;
        return this.addLogging(
          'GET',
          this.http.get<ApiPaginatedResponse<Project[]>>(url, { headers, params })
        );
      })
    );
  }

  listConfiguredModules(filters: ListConfiguredModulesFilters = {}): Observable<ApiPaginatedResponse<ConfiguredModuleRow[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();

        if (filters.invoicedStatus && filters.invoicedStatus.length > 0) {
          params = params.set('invoicedStatus', filters.invoicedStatus.join(','));
        }
        if (filters.companyName) {
          params = params.set('companyName', filters.companyName);
        }
        if (filters.status) {
          params = params.set('status', filters.status);
        }
        if (filters.moduleType) {
          params = params.set('moduleType', filters.moduleType);
        }
        if (filters.limit) {
          params = params.set('limit', String(filters.limit));
        }
        if (filters.startKey) {
          params = params.set('startKey', filters.startKey);
        }

        const url = `${this.apiUrl}/modules`;
        return this.addLogging(
          'GET',
          this.http.get<ApiPaginatedResponse<ConfiguredModuleRow[]>>(url, { headers, params })
        );
      })
    );
  }

  // Fixed: Use correct endpoint for getting projects by company
  getProjectsByCompany(companyID: string, limit?: number, lastEvaluatedKey?: string): Observable<ApiPaginatedResponse<Project[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = this.getPaginationParams(limit, lastEvaluatedKey);
        const url = `${this.apiUrl}/companies/${companyID}/projects`;
        return this.addLogging(
          'GET',
          this.http.get<ApiPaginatedResponse<Project[]>>(url, { headers, params })
        );
      }),
      tap(response => {
        if (response?.data?.length) {
          this.seedProjectCache(response.data);
        }
      })
    );
  }

  // Get projects assigned to a specific user
  getProjectsByUser(userID: string, limit?: number, lastEvaluatedKey?: string): Observable<ApiPaginatedResponse<Project[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = this.getPaginationParams(limit, lastEvaluatedKey);
        const url = `${this.apiUrl}/users/${userID}/projects`;
        return this.addLogging(
          'GET',
          this.http.get<ApiPaginatedResponse<Project[]>>(url, { headers, params })
        );
      }),
      tap(response => {
        if (response?.data?.length) {
          this.seedProjectCache(response.data);
        }
      })
    );
  }

  /**
   * Stores list-shaped project rows in the in-memory cache so `GET /projects/{id}` can resolve
   * immediately after the user picks a project from a list view.
   */
  seedProjectCache(projects: Project[]): void {
    for (const raw of projects) {
      const id = raw?.projectID?.trim();
      if (!id) {
        continue;
      }
      if (!this.projectCache.has(id)) {
        this.projectCache.set(id, stripProjectModuleEmbeds(raw));
      }
    }
  }

  /** Returns a cached project row when available (does not trigger an API call). */
  getCachedProject(projectID: string): Project | null {
    return this.projectCache.get(projectID) ?? null;
  }

  getProject(projectID: string, forceRefresh: boolean = false): Observable<ApiResponse<Project>> {
    // Check cache first (unless forceRefresh is true)
    if (!forceRefresh && this.projectCache.has(projectID)) {
      const cachedProject = this.projectCache.get(projectID)!;
      this.logger.debug('[CACHE HIT] Project', projectID, cachedProject);
      return of({ data: cachedProject, message: 'Project loaded from cache', code: ApiErrorCode.Success });
    }

    // If forceRefresh is true, remove from cache to force fresh load
    if (forceRefresh) {
      this.projectCache.delete(projectID);
      this.logger.debug('[CACHE CLEARED] Project', projectID, 'for force refresh');
    }

    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}`;
        // High-level project only; load `piled` via GET /projects/{id}/piled and Pull Testing via GET .../pull-testing.
        this.logger.debug('[API OUT] GET', url, { headers, forceRefresh });
        return this.http.get<ApiResponse<Project>>(url, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] GET', url, response);
            if (response.data) {
              this.projectCache.set(projectID, stripProjectModuleEmbeds(response.data));
            }
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * GET `/projects/{projectID}/piled` — full Piled module config (404 if not created).
   * Normalizes JSON-string `pilingAttributes` / `reworkAttributes` when the API returns legacy shape.
   */
  getPiledModule(projectID: string): Observable<ApiResponse<PiledModule>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/piled`;
        return this.addLogging('GET', this.http.get<ApiResponse<PiledModule>>(url, { headers })).pipe(
          map((response: ApiResponse<PiledModule>) =>
            response.data
              ? { ...response, data: this.normalizePiledModuleApiShape(response.data) }
              : response
          )
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Some responses still carry `pilingAttributes` / `reworkAttributes` as a JSON string (Dynamo S field).
   */
  private normalizePiledModuleApiShape(piled: PiledModule): PiledModule {
    const out: PiledModule = { ...piled };
    for (const key of ['pilingAttributes', 'reworkAttributes'] as const) {
      const raw = piled[key] as unknown;
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            (out as unknown as Record<string, unknown>)[key] = parsed;
          }
        } catch {
          /* leave as-is */
        }
      }
    }
    return out;
  }

  createPiledModule(projectID: string, body: CreatePiledModuleRequest): Observable<ApiResponse<PiledModule>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/piled`;
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<PiledModule>>(url, body, { headers })
        );
      }),
      catchError(this.handleError)
    );
  }

  createPullTestingModule(
    projectID: string,
    body: CreatePullTestingModuleRequest
  ): Observable<ApiResponse<PullTestingModule>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/pull-testing`;
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<PullTestingModule>>(url, body, { headers })
        );
      }),
      catchError(this.handleError)
    );
  }

  updatePullTestingModule(
    projectID: string,
    body: UpdatePullTestingModuleRequest
  ): Observable<ApiResponse<PullTestingModule>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/pull-testing`;
        return this.addLogging(
          'PUT',
          this.http.put<ApiResponse<PullTestingModule>>(url, body, { headers })
        );
      }),
      catchError(this.handleError)
    );
  }

  updatePiledModule(projectID: string, body: UpdatePiledModuleRequest): Observable<ApiResponse<PiledModule>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/piled`;
        return this.addLogging(
          'PUT',
          this.http.put<ApiResponse<PiledModule>>(url, body, { headers })
        );
      }),
      catchError(this.handleError)
    );
  }

  createProject(projectData: Partial<Project>): Observable<ApiResponse<Project>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects`;
        this.logger.debug('[API OUT] POST', url, { headers, body: projectData });
        return this.http.post<ApiResponse<Project>>(url, projectData, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] POST', url, response);
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  deleteProject(projectID: string): Observable<ApiResponse<void>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}`;
        return this.addLogging(
          'DELETE',
          this.http.delete<ApiResponse<void>>(url, { headers })
        );
      })
    );
  }

  updateProject(projectID: string, updateData: any): Observable<ApiResponse<Project>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}`;
        return this.addLogging(
          'PUT',
          this.http.put<ApiResponse<Project>>(url, updateData, { headers })
        ).pipe(
          tap(response => {
            // Clear the cache for this project when it's updated
            if (response.data) {
              this.projectCache.delete(projectID);
              this.logger.debug('[CACHE CLEARED] Project', projectID, 'after update');
            }
          })
        );
      })
    );
  }

  // Clear cache for a specific project
  clearProjectCache(projectID: string): void {
    this.projectCache.delete(projectID);
    this.logger.debug('[CACHE CLEARED] Project', projectID);
  }

  // Company-Project Assignment Methods
  getCompanyProjectAssignmentsByProjectID(projectID: string): Observable<ApiResponse<CompanyProjectAssignment[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/companies`;
        return this.addLogging(
          'GET',
          this.http.get<ApiResponse<CompanyProjectAssignment[]>>(url, { headers })
        );
      })
    );
  }

  // Get full company objects for a project
  getCompaniesByProject(projectID: string): Observable<ApiPaginatedResponse<ProjectCompany[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/companies`;
        return this.addLogging(
          'GET',
          this.http.get<ApiPaginatedResponse<ProjectCompany[]>>(url, { headers })
        );
      })
    );
  }

  getCompanyProjectAssignmentsByCompanyID(companyID: string): Observable<ApiResponse<CompanyProjectAssignment[]>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/companies/${companyID}/projects`;
        return this.addLogging(
          'GET',
          this.http.get<ApiResponse<CompanyProjectAssignment[]>>(url, { headers })
        );
      })
    );
  }

  assignCompanyToProject(projectID: string, companyID: string): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/companies/${companyID}`;
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<any>>(url, {}, { headers })
        );
      })
    );
  }

  removeCompanyFromProject(projectID: string, companyID: string): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/companies/${companyID}`;
        return this.addLogging(
          'DELETE',
          this.http.delete<ApiResponse<any>>(url, { headers })
        );
      })
    );
  }

  /** Assign an existing user to a project — `POST /users/{userID}/projects/{projectID}`. */
  assignUserToProject(projectID: string, userID: string): Observable<ApiResponse<void>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}/projects/${projectID}`;
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<void>>(url, {}, { headers })
        );
      })
    );
  }

  // Get user project assignment details
  getUserProjectAssignment(
    userID: string,
    projectID: string,
    forceRefresh: boolean = false
  ): Observable<ApiResponse<UserProjectAssignment>> {
    const cacheKey = `${userID}-${projectID}`;

    if (forceRefresh) {
      this.userProjectAssignmentCache.delete(cacheKey);
    } else if (this.userProjectAssignmentCache.has(cacheKey)) {
      const cachedAssignment = this.userProjectAssignmentCache.get(cacheKey)!;
      this.logger.debug('[CACHE HIT] User Project Assignment', cacheKey, cachedAssignment);
      return of({ data: cachedAssignment, message: 'User project assignment loaded from cache', code: ApiErrorCode.Success });
    }

    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}/projects/${projectID}`;
        this.logger.debug('[API OUT] GET', url, { headers });
        return this.http.get<ApiResponse<UserProjectAssignment>>(url, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] GET', url, response);
            // Cache the user project assignment data
            if (response.data) {
              this.userProjectAssignmentCache.set(cacheKey, response.data);
            }
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  // Update user project assignment (e.g., to update lastViewedTimestamp)
  updateUserProjectAssignment(userID: string, projectID: string, updateData: Partial<UserProjectAssignment>): Observable<ApiResponse<UserProjectAssignment>> {
    const cacheKey = `${userID}-${projectID}`;
    
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}/projects/${projectID}`;
        this.logger.debug('[API OUT] PUT', url, { headers, body: updateData });
        return this.http.put<ApiResponse<UserProjectAssignment>>(url, updateData, { headers }).pipe(
          tap(response => {
            this.logger.debug('[API IN] PUT', url, response);
            // Update cache with new data
            if (response.data) {
              this.userProjectAssignmentCache.set(cacheKey, response.data);
            }
          })
        );
      }),
      catchError(this.handleError)
    );
  }

  removeUserFromProject(projectID: string, userID: string): Observable<ApiResponse<void>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/users/${userID}/projects/${projectID}`;
        return this.addLogging(
          'DELETE',
          this.http.delete<ApiResponse<void>>(url, { headers })
        );
      })
    );
  }

  /**
   * Users the current user may add to this project, grouped by company.
   * Prefers `GET /projects/{projectID}/directory`; callers may fall back to company user lists on 404.
   */
  getProjectDirectory(projectID: string): Observable<ApiResponse<ProjectDirectory>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/directory`;
        return this.addLogging('GET', this.http.get<ApiResponse<ProjectDirectory>>(url, { headers }));
      })
    );
  }

  /**
   * Add or invite a user by email via `POST /projects/{projectID}/invite-viewer`.
   * If the email already has an account, that user is added to the project with their role.
   */
  inviteProjectUser(projectID: string, email: string): Observable<ApiResponse<unknown>> {
    return this.inviteViewerToProject(projectID, email);
  }

  /** `POST /projects/{projectID}/invite-viewer` with body `{ email }`. */
  inviteViewerToProject(projectID: string, email: string): Observable<ApiResponse<unknown>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/invite-viewer`;
        const body = { email };
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<unknown>>(url, body, { headers })
        );
      })
    );
  }

  getProjectStatus(projectID: string): Observable<ApiResponse<ProjectStatusModel>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/piled/status`;
        return this.addLogging('GET', this.http.get<ApiResponse<ProjectStatusModel>>(url, { headers }));
      })
    );
  }

  /**
   * Bulk-update CSV: presigned PUT to S3 (`POST /projects/{projectID}/piled/bulk-update-csv`).
   * Same flow as {@link uploadProjectCSV}; different bucket/target per backend.
   */
  uploadBulkUpdateCSV(projectID: string, csvFile: File): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const presignedUrlEndpoint = `${this.apiUrl}/projects/${projectID}/piled/bulk-update-csv`;
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<{ presignedUrl: string; key: string }>>(
            presignedUrlEndpoint,
            null,
            { headers }
          )
        ).pipe(
          switchMap(response => {
            if (!response.data?.presignedUrl) {
              throw new Error('Failed to get presigned URL from server');
            }
            return from(this.uploadToS3(response.data.presignedUrl, csvFile)).pipe(
              map(() => ({
                ...response,
                data: { key: response.data!.key },
              })),
              catchError(error => {
                this.logger.error('[S3 UPLOAD] Bulk update upload failed:', error);
                throw new Error(`Failed to upload file to S3: ${error.message}`);
              })
            );
          }),
          catchError(error => {
            this.logger.error('[API ERROR] Bulk update presigned URL request:', error);
            throw error;
          })
        );
      })
    );
  }

  // CSV Upload Method - Uses presigned URL for direct S3 upload
  uploadProjectCSV(projectID: string, csvFile: File): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        // Step 1: Request presigned URL from backend
        const presignedUrlEndpoint = `${this.apiUrl}/projects/${projectID}/piled/upload-csv`;
        
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<{ presignedUrl: string; key: string }>>(
            presignedUrlEndpoint, 
            null,
            { headers }
          )
        ).pipe(
          switchMap(response => {
            if (!response.data?.presignedUrl) {
              throw new Error('Failed to get presigned URL from server');
            }
            
            // Step 2: Upload file directly to S3 using presigned URL
            return from(this.uploadToS3(response.data.presignedUrl, csvFile)).pipe(
              map(() => ({
                ...response,
                data: { key: response.data.key }
              })),
              catchError(error => {
                this.logger.error('[S3 UPLOAD] Upload failed:', error);
                throw new Error(`Failed to upload file to S3: ${error.message}`);
              })
            );
          }),
          catchError(error => {
            this.logger.error('[API ERROR] Presigned URL Request Error:', error);
            throw error;
          })
        );
      })
    );
  }

  // Upload file directly to S3 using presigned URL
  private uploadToS3(presignedUrl: string, file: File): Promise<void> {
    return fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'text/csv'
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
      }
    });
  }

  // Upload image file directly to S3 using presigned URL
  private uploadImageToS3(presignedUrl: string, file: File): Promise<void> {
    return fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'image/png'
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
      }
    });
  }

  // Map Image Upload Method - Uses presigned URL for direct S3 upload
  uploadMapImage(projectID: string, imageFile: File): Observable<ApiResponse<{ key: string }>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        // Step 1: Request presigned URL from backend
        const presignedUrlEndpoint = `${this.apiUrl}/projects/${projectID}/upload-map-image`;
        
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<{ presignedUrl: string; key: string }>>(
            presignedUrlEndpoint, 
            null,
            { headers }
          )
        ).pipe(
          switchMap(response => {
            if (!response.data?.presignedUrl) {
              throw new Error('Failed to get presigned URL from server');
            }
            
            // Step 2: Upload file directly to S3 using presigned URL
            return from(this.uploadImageToS3(response.data.presignedUrl, imageFile)).pipe(
              map(() => ({
                ...response,
                data: { key: response.data.key }
              })),
              catchError(error => {
                this.logger.error('[S3 UPLOAD] Image upload failed:', error);
                throw new Error(`Failed to upload image to S3: ${error.message}`);
              })
            );
          }),
          catchError(error => {
            this.logger.error('[API ERROR] Presigned URL Request Error:', error);
            throw error;
          })
        );
      })
    );
  }

  // Generate PDF from map image and send via email
  // Now always uses backend rendering for consistent image sizes
  // Backend automatically calculates project status counts and anticipated drive time statistics
  generateMapPDF(
    projectID: string, 
    imageS3Key: string | undefined, // Deprecated: no longer used, kept for backward compatibility
    emails: string[], 
    timezone?: string, 
    fitAllPilings: boolean = false,
    includeAnticipatedDriveTime: boolean = false,
    mapState?: MapState | null // Map state for custom view rendering (when fitAllPilings is false)
  ): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/${projectID}/piled/generate-map-pdf`;
        const body: any = {
          emails,
          fitAllPilings,
          includeAnticipatedDriveTime
        };
        
        // Include map state for custom view rendering (when fitAllPilings is false)
        // Backend will use this to render the same geographic view at a standard size
        if (!fitAllPilings && mapState) {
          body.mapState = mapState;
        }
        
        // Legacy: Only include imageS3Key for backward compatibility (should not be used)
        // Backend should ignore this when mapState is provided
        if (!fitAllPilings && imageS3Key && !mapState) {
          body.imageS3Key = imageS3Key;
        }
        
        // Add timezone if provided
        if (timezone) {
          body.timezone = timezone;
        }
        
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<any>>(url, body, { headers })
        );
      })
    );
  }

  // Legacy method for backward compatibility - now uses the new structure
  getProjects(companyID: string, limit?: number, lastEvaluatedKey?: string): Observable<ApiPaginatedResponse<Project[]>> {
    return this.getProjectsByCompany(companyID, limit, lastEvaluatedKey);
  }

  // Get daily progress statistics for a project
  getDailyProgress(projectID: string, startTimestamp: string, endTimestamp: string): Observable<ApiResponse<DailyProgressData>> {
    // Helper to check if a date string (ISO or YYYY-MM-DD) is today
    function isToday(dateStr: string): boolean {
      const today = new Date();
      const date = new Date(dateStr);
      return date.getFullYear() === today.getFullYear() &&
             date.getMonth() === today.getMonth() &&
             date.getDate() === today.getDate();
    }
    // If either timestamp is today, always fetch
    if (isToday(startTimestamp) || isToday(endTimestamp)) {
      return from(this.authService.getHeaders()).pipe(
        switchMap(headers => {
          const params = new HttpParams()
            .set('startTimestamp', startTimestamp)
            .set('endTimestamp', endTimestamp);
          const url = `${this.apiUrl}/projects/${projectID}/piled/dailyProgress`;
          return this.addLogging(
            'GET',
            this.http.get<ApiResponse<DailyProgressData>>(url, { headers, params })
          );
        })
      );
    }
    // Otherwise, use cache if available
    const cacheKey = `${projectID}|${startTimestamp}|${endTimestamp}`;
    if (this.dailyProgressCache.has(cacheKey)) {
      return of(this.dailyProgressCache.get(cacheKey)!);
    }
    // Not cached, fetch and cache
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const params = new HttpParams()
          .set('startTimestamp', startTimestamp)
          .set('endTimestamp', endTimestamp);
        const url = `${this.apiUrl}/projects/${projectID}/piled/dailyProgress`;
        return this.addLogging(
          'GET',
          this.http.get<ApiResponse<DailyProgressData>>(url, { headers, params })
        );
      }),
      tap(response => {
        this.dailyProgressCache.set(cacheKey, response);
      })
    );
  }

  // Request a new project
  requestProject(projectRequest: ProjectRequest, csvFile?: File): Observable<ApiResponse<ProjectRequestResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/request`;
        
        // Create FormData for multipart/form-data
        const formData = new FormData();
        
        // Debug: Log the actual values before appending
        this.logger.debug('🔍 DEBUG: Values being appended to FormData:');
        this.logger.debug('  firstName:', projectRequest.firstName, 'Type:', typeof projectRequest.firstName);
        this.logger.debug('  lastName:', projectRequest.lastName, 'Type:', typeof projectRequest.lastName);
        this.logger.debug('  company:', projectRequest.company, 'Type:', typeof projectRequest.company);
        this.logger.debug('  phoneNumber:', projectRequest.phoneNumber, 'Type:', typeof projectRequest.phoneNumber);
        this.logger.debug('  email:', projectRequest.email, 'Type:', typeof projectRequest.email);
        this.logger.debug('  projectName:', projectRequest.projectName, 'Type:', typeof projectRequest.projectName);
        this.logger.debug('  projectAddress:', projectRequest.projectAddress, 'Type:', typeof projectRequest.projectAddress);
        this.logger.debug('  totalPilings:', projectRequest.totalPilings, 'Type:', typeof projectRequest.totalPilings);
        if (projectRequest.numberOfArrays != null) {
          this.logger.debug('  numberOfArrays:', projectRequest.numberOfArrays, 'Type:', typeof projectRequest.numberOfArrays);
        }
        this.logger.debug('  anticipatedStartDate:', projectRequest.anticipatedStartDate, 'Type:', typeof projectRequest.anticipatedStartDate);
        this.logger.debug('  pilingType:', projectRequest.pilingType, 'Type:', typeof projectRequest.pilingType);
        
        // Add all form fields with validation - use empty string for undefined values
        formData.append('firstName', projectRequest.firstName || '');
        formData.append('lastName', projectRequest.lastName || '');
        formData.append('company', projectRequest.company || '');
        formData.append('phoneNumber', projectRequest.phoneNumber || '');
        formData.append('email', projectRequest.email || '');
        formData.append('projectName', projectRequest.projectName || '');
        formData.append('projectAddress', projectRequest.projectAddress || '');
        formData.append('totalPilings', (projectRequest.totalPilings || 0).toString());
        if (projectRequest.numberOfArrays != null) {
          formData.append('numberOfArrays', String(projectRequest.numberOfArrays));
        }
        formData.append('anticipatedStartDate', projectRequest.anticipatedStartDate || '');
        formData.append('pilingType', projectRequest.pilingType || '');
        formData.append('pilingAttributes', JSON.stringify(projectRequest.pilingAttributes || []));
        formData.append('reworkAttributes', JSON.stringify(projectRequest.reworkAttributes || []));
        formData.append('quickHits', JSON.stringify(projectRequest.quickHits || []));
        formData.append('qcQuickHits', JSON.stringify(projectRequest.qcQuickHits || []));
        formData.append('reworkQuickHits', JSON.stringify(projectRequest.reworkQuickHits || []));
        formData.append('completedReworkQuickHits', JSON.stringify(projectRequest.completedReworkQuickHits || []));
        
        // Add user selection data
        if (projectRequest.selectedUsers) {
          formData.append('existingUsers', JSON.stringify(projectRequest.selectedUsers));
        }
        if (projectRequest.additionalUsers) {
          // Transform additionalUsers to match API expected format
          const newUsers = projectRequest.additionalUsers.map(user => ({
            firstName: user.fullName.split(' ')[0] || '',
            lastName: user.fullName.split(' ').slice(1).join(' ') || '',
            email: user.email,
            phoneNumber: '', // API expects this but we don't collect it
            role: user.role
          }));
          formData.append('newUsers', JSON.stringify(newUsers));
        }
        if (projectRequest.selectedExternalUsers) {
          // External users are also existing users, so add them to existingUsers
          const allExistingUsers = [
            ...(projectRequest.selectedUsers || []),
            ...(projectRequest.selectedExternalUsers || [])
          ];
          formData.append('existingUsers', JSON.stringify(allExistingUsers));
        }
        
        // Add CSV file if provided
        if (csvFile) {
          formData.append('csvFile', csvFile);
        }
        
        // Remove Content-Type header to let browser set it with boundary for multipart/form-data
        headers = headers.delete('Content-Type');
        
        // Debug: Log headers and request details
        this.logger.debug('🔍 DEBUG: Request details:', {
          url,
          headers: headers.keys().map(key => ({ key, value: headers.get(key) })),
          hasAuthHeader: headers.has('Authorization'),
          authHeaderValue: headers.get('Authorization')?.substring(0, 50) + '...',
          hasCSVFile: !!csvFile,
          csvFileName: csvFile?.name,
          firstName: projectRequest.firstName,
          lastName: projectRequest.lastName,
          email: projectRequest.email,
          formDataType: formData instanceof FormData ? 'FormData' : 'Not FormData'
        });
        
        // Log FormData contents for debugging
        this.logger.debug('🔍 DEBUG: FormData contents:');
        this.logger.debug('  FormData created successfully with fields:', [
          'firstName', 'lastName', 'company', 'phoneNumber', 'email', 
          'projectName', 'projectAddress', 'totalPilings', 'numberOfArrays',
          'anticipatedStartDate', 'pilingType', 'pilingAttributes', 
          'reworkAttributes', 'quickHits', 'qcQuickHits', 'reworkQuickHits',
          'completedReworkQuickHits', 'selectedUsers', 'additionalUsers', 'selectedExternalUsers'
        ]);
        if (csvFile) {
          this.logger.debug('  CSV File:', csvFile.name, 'Size:', csvFile.size);
        }
        
        return this.addLogging(
          'POST',
          this.http.post<ApiResponse<ProjectRequestResponse>>(url, formData, { headers })
        );
      })
    );
  }

  // Get list of project requests
  getProjectRequests(limit?: number, nextToken?: string): Observable<ApiResponse<ProjectRequestListResponse>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (limit) params = params.set('limit', limit.toString());
        if (nextToken) params = params.set('nextToken', nextToken);
        
        const url = `${this.apiUrl}/projects/requests`;
        return this.addLogging(
          'GET',
          this.http.get<ApiResponse<ProjectRequestListResponse>>(url, { headers, params })
        );
      })
    );
  }

  // Get detailed information about a specific project request
  getProjectRequestDetail(requestId: string): Observable<ApiResponse<ProjectRequestDetail>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        const url = `${this.apiUrl}/projects/request/${requestId}`;
        return this.addLogging(
          'GET',
          this.http.get<ApiResponse<ProjectRequestDetail>>(url, { headers })
        );
      })
    );
  }

  // Get recommended quick hits
  getRecommendedQuickHits(type?: string): Observable<ApiResponse<any>> {
    return from(this.authService.getHeaders()).pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (type) params = params.set('type', type);
        
        const url = `${this.apiUrl}/projects/quick-hits`;
        return this.addLogging(
          'GET',
          this.http.get<ApiResponse<any>>(url, { headers, params })
        );
      })
    );
  }
}